import { cn } from '@utils/cn';

import { useAnimatedMount } from '@ui-component-library/hooks/useAnimatedMount';

import {
  modalBackdropClasses,
  modalContentStyles,
  modalContentWrapperStyles,
  modalFooterClasses,
  modalHeaderClasses,
  modalWrapperClasses
} from './Modal.styles';
import type {
  ModalContentProps,
  ModalFooterProps,
  ModalHeaderProps,
  ModalProps
} from './Modal.types';

export const Modal = ({
  isOpen = false,
  children,
  tabIndex = 0,
  className,
  borderless = false,
  ...props
}: ModalProps) => {
  const { shouldRender, animateIn, ref, onTransitionEnd } = useAnimatedMount(isOpen);

  if (!shouldRender) {
    return null;
  }

  return (
    <div
      className={cn(modalBackdropClasses())}
      style={{
        opacity: animateIn ? 1 : 0,
        transition: 'opacity 300ms ease-out'
      }}
    >
      <div
        ref={ref}
        className={cn(modalWrapperClasses({ borderless }), className)}
        style={{
          opacity: animateIn ? 1 : 0,
          transform: animateIn ? 'scale(1)' : 'scale(0.95)',
          transition: 'opacity 300ms ease-out, transform 300ms ease-out'
        }}
        onTransitionEnd={onTransitionEnd}
        tabIndex={tabIndex}
        {...props}
      >
        {children}
      </div>
    </div>
  );
};

const ModalHeader = ({ children, className, ...props }: ModalHeaderProps) => (
  <div className={cn(modalHeaderClasses(), className)} {...props}>
    {children}
  </div>
);

const ModalContent = ({ children, className, ...props }: ModalContentProps) => (
  <div className={cn(modalContentWrapperStyles(), className)} {...props}>
    <div className={cn(modalContentStyles())}>{children}</div>
  </div>
);

const ModalFooter = ({ children, className, ...props }: ModalFooterProps) => (
  <div className={cn(modalFooterClasses(), className)} {...props}>
    {children}
  </div>
);

Modal.Header = ModalHeader;
Modal.Content = ModalContent;
Modal.Footer = ModalFooter;
